// ═══════════════════════════════════════════════════════════════════
//  useTelemetryFlush — Background flush of in-memory telemetry buffers.
// ═══════════════════════════════════════════════════════════════════
//
//  Schedules `flushTelemetryToSupabase` on a 30-second interval and
//  also on `beforeunload` (best-effort) so events buffered between
//  intervals aren't lost on tab close.
//
//  Mounted once near the top of the app tree (App.tsx). No-op when
//  the user is unauthenticated.
//
// ═══════════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { flushTelemetryToSupabase } from '@/lib/telemetryFlush';

const INTERVAL_MS = 30_000;

export function useTelemetryFlush(): void {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const flush = () => {
      if (cancelled) return;
      void flushTelemetryToSupabase(supabase, user.id);
    };

    const interval = window.setInterval(flush, INTERVAL_MS);
    const onUnload = () => flush();
    window.addEventListener('beforeunload', onUnload);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('beforeunload', onUnload);
      // Final flush on unmount (e.g. user signing out).
      flush();
    };
  }, [user]);
}
